// libs
import { useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
// components
import useUsers from '@/components/UsersSearch/hooks/useUsers';
import { GROUP_DEFAULT_FILTERS } from '@/modules/corporate/components/LearnerAndGroupDialog/utils';
import useGroups from '@/modules/corporate/modules/group/views/GroupManager/hooks/useGroups';
import { UserTypeEnum } from '@/types/schema';

import { LearnersListProps } from '../../types';

export default function useLearnerAndGroupList(props: LearnersListProps) {
  const {
    learnerIds,
    userGroupIds,
    onChange,
    removedUserIds = [],
    importedUserGroupIds = [],
  } = props;
  const [openLearnerAndGroup, { toggle: toggleLearnerAndGroup }] = useToggle(false);
  const [openSeeAll, { toggle: toggleSeeAll }] = useToggle(false);

  const stableImportedUserGroupIds = useMemo(
    () => [...importedUserGroupIds],
    [importedUserGroupIds],
  );
  const stableLearnerIds = useMemo(() => [...learnerIds], [learnerIds]);

  const OR = useMemo(() => {
    return stableImportedUserGroupIds?.length
      ? {
          groupIds: stableImportedUserGroupIds,
          userIds: stableLearnerIds,
        }
      : undefined;
  }, [stableImportedUserGroupIds, stableLearnerIds]);

  const {
    data: learners,
    isFetching: fetchingLearners,
    totalCount: totalUserCount = 0,
  } = useUsers({
    ids: OR ? undefined : learnerIds,
    pause: !learnerIds.length && !OR,
    userTypes: [UserTypeEnum.Learner],
    // groupIds: learnerIds?.length ? undefined : importedUserGroupIds,
    notInIds: removedUserIds,
    userGroupIds: importedUserGroupIds,
    OR,
  });
  const {
    groups,
    isFetching: fetchingUserGroups,
    totalCount: totalGroupCount = 0,
  } = useGroups({
    filters: {
      ids: userGroupIds,
      ...GROUP_DEFAULT_FILTERS,
    },
    pause: !userGroupIds.length,
  });

  const learnerListItems = useMemo(() => {
    if (!learners?.length) {
      return [];
    }

    return learners;
  }, [learnerIds.length, learners]);

  const userGroupListItems = useMemo(() => {
    if (!userGroupIds.length || !groups?.length) {
      return [];
    }

    return groups;
  }, [userGroupIds.length, groups]);

  const hasMoreLearners = learnerListItems.length < totalUserCount;
  const hasMoreUserGroups = userGroupListItems.length < totalGroupCount;
  const hasAnyItems = userGroupListItems.length + learnerListItems.length > 0;

  const showSeeMore = (hasMoreLearners || hasMoreUserGroups) && hasAnyItems;

  const onAddLearners = useCallback(
    (
      selectedLearnerIds: any,
      selectedUserGroupIds: any,
      importedUserGroupIds: any,
      removedUserIds: any,
    ) => {
      onChange({
        userIds: selectedLearnerIds,
        userGroupIds: selectedUserGroupIds,
        importedUserGroupIds,
        removedUserIds,
      });
      toggleLearnerAndGroup();
    },
    [onChange, toggleLearnerAndGroup],
  );

  const onRemoveUser = useCallback(
    ({ removedUserId, isInGroups }: any) => {
      onChange({
        userIds: learnerIds.filter((learnerId) => learnerId !== removedUserId),
        userGroupIds,
        removedUserIds: [...removedUserIds, ...(isInGroups ? [removedUserId] : [])],
      });
    },
    [onChange, learnerIds, userGroupIds],
  );

  const onRemovegroup = useCallback(
    ({ removedGroupId: deletedCollectionId }: any) => {
      onChange({ userGroupIds: userGroupIds.filter((groupId) => groupId !== deletedCollectionId) });
    },
    [onChange, userGroupIds],
  );

  return {
    learnerListItems,
    onRemoveUser,
    userGroupListItems,
    onRemovegroup,
    showSeeMore,
    toggleSeeAll,
    openLearnerAndGroup,
    toggleLearnerAndGroup,
    onAddLearners,
    openSeeAll,
    totalUserCount,
  };
}

export type UseLearnerAndGroupList = ReturnType<typeof useLearnerAndGroupList>;
